import { useEffect, useState, useRef } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { MessageCircle, ChefHat, Sparkles, Car, Send } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = [
  { id: "chef", label: "Private chef", Icon: ChefHat, hint: "I'd like to arrange a private chef for..." },
  { id: "spa", label: "In-villa spa", Icon: Sparkles, hint: "Please arrange an in-villa spa treatment on..." },
  { id: "transfer", label: "Airport transfer", Icon: Car, hint: "Could you organise an airport transfer for..." },
  { id: "general", label: "Something else", Icon: MessageCircle, hint: "How can we make your stay perfect?" },
];

export default function ConciergeDrawer({ booking }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [cat, setCat] = useState("general");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get(`/bookings/${booking.id}/concierge`).then((r) => setMsgs(r.data));
  }, [open, booking.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/concierge`, { message: text, category: cat });
      setMsgs((m) => [...m, ...data]);
      setText("");
    } finally { setSending(false); }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<MessageCircle size={12} />}
        onClick={() => setOpen(true)}
        data-testid={`concierge-btn-${booking.id}`}
        sx={{ fontSize: 12 }}
      >
        Concierge
      </Button>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: { xs: "100vw", sm: 420 }, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
            <Typography className="font-serif-display" sx={{ fontSize: 24 }}>Nirvagoo Concierge</Typography>
            <Typography variant="caption" color="text.secondary">{booking.villa_name}</Typography>
          </Box>

          <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", p: 3, display: "flex", flexDirection: "column", gap: 2, bgcolor: "action.hover" }}>
            {msgs.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Box className="bg-gold" sx={{ width: 56, height: 56, borderRadius: "50%", opacity: 0.15, mx: "auto", mb: 2 }} />
                <Typography className="font-serif-display" sx={{ fontSize: 18, mb: 1, mt: -7 }}>
                  <MessageCircle size={20} className="text-gold" style={{ display: "block", margin: "0 auto 12px" }} />
                  How can we serve you?
                </Typography>
                <Typography variant="caption" color="text.secondary">Our on-property team is available 24/7 to arrange anything for your stay.</Typography>
              </Box>
            )}
            {msgs.map((m) => (
              <Box key={m.id} data-testid={`concierge-msg-${m.id}`} sx={{ display: "flex", justifyContent: m.from === "guest" ? "flex-end" : "flex-start" }}>
                <Box
                  sx={{
                    maxWidth: "80%", borderRadius: 4, px: 2, py: 1.5, fontSize: 14,
                    bgcolor: m.from === "guest" ? "primary.main" : "background.paper",
                    color: m.from === "guest" ? "primary.contrastText" : "text.primary",
                    border: m.from === "guest" ? 0 : 1, borderColor: "divider",
                  }}
                >
                  {m.from === "concierge" && <Eyebrow sx={{ fontSize: 8, mb: 0.5, color: "inherit", opacity: 0.7 }}>Concierge</Eyebrow>}
                  {m.message}
                </Box>
              </Box>
            ))}
          </Box>

          <Box component="form" onSubmit={send} sx={{ p: 2, borderTop: 1, borderColor: "divider", display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box className="no-scrollbar" sx={{ display: "flex", gap: 0.75, overflowX: "auto" }}>
              {CATEGORIES.map((c) => (
                <Button
                  key={c.id}
                  type="button"
                  size="small"
                  variant={cat === c.id ? "contained" : "outlined"}
                  onClick={() => { setCat(c.id); if (!text) setText(c.hint); }}
                  data-testid={`concierge-cat-${c.id}`}
                  startIcon={<c.Icon size={11} />}
                  sx={{ flexShrink: 0, fontSize: 11 }}
                >
                  {c.label}
                </Button>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your request…"
                inputProps={{ "data-testid": `concierge-input-${booking.id}` }}
              />
              <IconButton
                type="submit"
                disabled={sending || !text.trim()}
                data-testid={`concierge-send-${booking.id}`}
                sx={{ bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.main" }, "&.Mui-disabled": { opacity: 0.5 } }}
              >
                <Send size={15} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

function Eyebrow({ children, sx }) {
  return (
    <Box className="eyebrow" sx={{ textTransform: "uppercase", ...sx }}>{children}</Box>
  );
}
